import { Badge } from 'components/Badge/Badge'
import { FilterEnum, IFilter } from 'components/Filter/SingleFilter'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Cell, RightCell } from 'components/HonestTable/HonestTable.styled'
import { NavBar } from 'components/NavBar/NavBar'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, ColoredDot, Flex, MainHeadline, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText } from 'styling/Typography'
import {
    IOperationTriggerRow,
    OperationTriggerAction,
    OperationTriggerPriority,
    OperationTriggerType,
    TRIGGER_MODE_COLORS,
} from 'types/operationTriggers.interface'
import { getParams } from 'utils/helpers'
import { MOCK_OPERATION_TRIGGERS } from './operationTriggersData'

const formatMetadata = (item: IOperationTriggerRow) => {
    const parts: string[] = [item.priority]
    if (item.siteId !== undefined) parts.push(`Site #${item.siteId}`)
    if (item.departmentId !== undefined) parts.push(`Dept #${item.departmentId}`)
    return parts.join(' · ')
}

const formatFires = (item: IOperationTriggerRow) => {
    const fires = `${item.fireCount} ${item.fireCount === 1 ? 'fire' : 'fires'}`
    return item.lastFired ? `${fires} · ${item.lastFired}` : `${fires} · Never`
}

const columns: IHonestTableColumn<IOperationTriggerRow>[] = [
    {
        id: 'type',
        header: 'Type',
        sticky: true,
        minWidth: '8rem',
        cell: (item) => (
            <Badge text={item.type} green={item.type === 'Completion'} orange={item.type === 'Scheduled'} red={item.type === 'Threshold'} />
        ),
    },
    {
        id: 'trigger',
        header: 'Trigger',
        minWidth: '18rem',
        cell: (item) => (
            <Cell>
                <BodyText weight='600'>{item.title}</BodyText>
                <OtherText grey>{formatMetadata(item)}</OtherText>
            </Cell>
        ),
    },
    {
        id: 'action',
        header: 'Action',
        minWidth: '10rem',
        cell: (item) => <BodyText>{item.action}</BodyText>,
    },
    {
        id: 'fires',
        header: 'Fires',
        minWidth: '10rem',
        cell: (item) => <OtherText grey>{formatFires(item)}</OtherText>,
    },
    {
        id: 'spacer',
        header: '',
        fill: true,
        cell: () => null,
    },
    {
        id: 'status',
        header: 'Status',
        align: 'right',
        minWidth: '8rem',
        cell: (item) => <TriggerStatusBadge active={item.active} />,
    },
]

const TriggerStatusBadge = ({ active }: { active: boolean }) => {

    return (
        <RightCell>
            <Badge text={active ? 'ACTIVE' : 'INACTIVE'} green={active} orange={!active} />
        </RightCell>
    )
}

const MODE_BY_FILTER_ID: Record<string, OperationTriggerType> = {
    '2': 'Completion',
    '3': 'Scheduled',
    '4': 'Threshold',
}

const ACTION_BY_FILTER_ID: Record<string, OperationTriggerAction> = {
    '2': 'Create task',
    '3': 'Create workflow',
    '4': 'Raise issue',
    '5': 'Notify role',
}

const PRIORITY_BY_FILTER_ID: Record<string, OperationTriggerPriority> = {
    '2': 'normal',
    '3': 'high',
    '4': 'critical',
}

const filterTriggers = (rows: IOperationTriggerRow[], params: Record<string, unknown>) =>
    rows.filter((row) => {
        if (params.mode && row.type !== MODE_BY_FILTER_ID[String(params.mode)]) return false
        if (params.status === '2' && !row.active) return false
        if (params.status === '3' && row.active) return false
        if (params.action && row.action !== ACTION_BY_FILTER_ID[String(params.action)]) return false
        if (params.priority && row.priority !== PRIORITY_BY_FILTER_ID[String(params.priority)]) return false
        return true
    })

const filters: IFilter[] = [
    {
        type: FilterEnum.FIXED_LIST,
        name: 'Mode',
        state: null,
        propName: 'mode',
        sd: true,
        single: true,
        filterOptions: [
            { name: 'Completion', id: '2' },
            { name: 'Scheduled', id: '3' },
            { name: 'Threshold', id: '4' },
        ],
    },
    {
        type: FilterEnum.FIXED_LIST,
        name: 'Status',
        state: null,
        propName: 'status',
        sd: true,
        single: true,
        filterOptions: [
            { name: 'Active', id: '2' },
            { name: 'Inactive', id: '3' },
        ],
    },
    {
        type: FilterEnum.FIXED_LIST,
        name: 'Action',
        state: null,
        propName: 'action',
        sd: true,
        single: true,
        filterOptions: [
            { name: 'Create Task', id: '2' },
            { name: 'Create Workflow', id: '3' },
            { name: 'Raise Issue', id: '4' },
            { name: 'Notify Role', id: '5' },
        ],
    },
    {
        type: FilterEnum.FIXED_LIST,
        name: 'Priority',
        state: null,
        propName: 'priority',
        sd: true,
        single: true,
        filterOptions: [
            { name: 'Normal', id: '2' },
            { name: 'High', id: '3' },
            { name: 'Critical', id: '4' },
        ],
    },
]

export const OperationTriggers = () => {
    const navigate = useNavigate()
    const [params, setParams] = useState<any>(getParams({ location }))
    const filteredTriggers = useMemo(
        () => filterTriggers(MOCK_OPERATION_TRIGGERS, params),
        [params],
    )

    return (
        <>
            <NavBar
                enableTemporaryManualClosing
                filterOptions={filters}
                params={params}
                setParams={setParams}
            />
            <PageWrapper>
                <Flex width='100%' between mb='32'>
                    <MainHeadline>Operation Triggers</MainHeadline>
                    <Button orange onClick={() => navigate('add')}>
                        Add
                    </Button>
                </Flex>
                <HonestTable columns={columns} data={filteredTriggers} keyField='id' />
            </PageWrapper>
        </>
    )
}
